import React, { useState, useEffect } from 'react';
import axios from '../config/api';
import { useAuth } from '../contexts/AuthContext';
import { useNotification } from '../contexts/NotificationContext';

const Products = () => {
  const { user, updateUser } = useAuth(); // Changed from refreshUser to updateUser which is the correct function
  const { success, error } = useNotification();
  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [purchasing, setPurchasing] = useState(null);
  const [showPurchaseModal, setShowPurchaseModal] = useState(false);
  const [selectedProduct, setSelectedProduct] = useState(null);
  const [selectedDuration, setSelectedDuration] = useState(1);

  useEffect(() => {
    fetchProducts();
  }, []); // Remove updateUser and user from dependencies

  useEffect(() => {
    if (user) {
      const fetchUserProfile = async () => {
        try {
          const response = await axios.get('/api/users/profile');
          if (JSON.stringify(response.data) !== JSON.stringify(user)) {
            updateUser(response.data);
          }
        } catch (err) {
          console.error('Failed to refresh user data:', err);
        }
      };

      fetchUserProfile();
    }
  }, []); // Empty dependency array - runs once on mount

  const fetchProducts = async () => {
    try {
      setLoading(true);
      const response = await axios.get('/api/products');
      setProducts(response.data.filter(p => p.isActive));
    } catch (err) {
      console.error('Failed to fetch products:', err);
      error('Failed to load products');
    } finally {
      setLoading(false);
    }
  };

  const calculateCost = (product, days) => {
    return product.baseCreditCost + (days * product.creditsPerDay);
  };

  const handlePurchase = async () => {
    if (!selectedProduct) return;

    setPurchasing(selectedProduct._id);
    try {
      const totalCost = calculateCost(selectedProduct, selectedDuration);

      console.log('Purchasing:', {
        productId: selectedProduct._id,
        duration: selectedDuration,
        totalCost,
        userCredits: user?.credits
      });

      const response = await axios.post('/api/keys/purchase', {
        productId: selectedProduct._id,
        duration: selectedDuration
      });

      console.log('Purchase response:', response.data);

      // Show success message with key
      success(`Successfully purchased ${selectedProduct.name} for ${selectedDuration} days!\nKey: ${response.data.licenseKey.key}\nCredits used: ${calculateCost(selectedProduct, selectedDuration)}`, 8000);

      // Update user credits from response
      if (response.data.remainingCredits !== undefined) {
        updateUser({ ...user, credits: response.data.remainingCredits });
      } else {
        // Fallback: manually calculate new credit amount
        updateUser({ ...user, credits: user.credits - response.data.remainingCredits });
      }

      setShowPurchaseModal(false);
      setSelectedProduct(null);
      setSelectedDuration(1);
    } catch (err) {
      console.error('Purchase error:', err);
      console.error('Error response:', err.response?.data);

      const errorMessage = err.response?.data?.message || 'Purchase failed';
      const errorDetails = err.response?.data;

      if (errorDetails?.breakdown) {
        error(`${errorMessage}\nRequired: ${errorDetails.required} credits\nAvailable: ${errorDetails.available} credits\nBase cost: ${errorDetails.breakdown.baseCost}, Duration cost: ${errorDetails.breakdown.durationCost} (${errorDetails.breakdown.duration} days)`);
      } else {
        error(errorMessage);
      }
    } finally {
      setPurchasing(null);
    }
  };

  const openPurchaseModal = (product) => {
    setSelectedProduct(product);
    setSelectedDuration(product.minDays || 1);
    setShowPurchaseModal(true);
  };

  const handleRefreshBalance = async () => {
    try {
      const response = await axios.get('/api/users/profile');
      updateUser(response.data);
      success('Balance refreshed!', 2000);
    } catch (err) {
      error('Failed to refresh balance');
    }
  };

  const getTypeColor = (type) => {
    switch (type) {
      case 'software':
        return 'text-blue-400 bg-blue-600/20';
      case 'game':
        return 'text-green-400 bg-green-600/20';
      case 'service':
        return 'text-purple-400 bg-purple-600/20';
      default:
        return 'text-gray-400 bg-gray-600/20';
    }
  };

  const ProductCard = ({ product }) => {
    const sampleCosts = [
      { days: 1, cost: calculateCost(product, 1) },
      { days: 7, cost: calculateCost(product, 7) },
      { days: 30, cost: calculateCost(product, 30) },
      { days: 90, cost: calculateCost(product, 90) }
    ];

    const canAfford = (days) => {
      const cost = calculateCost(product, days);
      return user && user.credits >= cost;
    };

    return (
      <div className="card-astral p-6 hover:glow-purple transition-all duration-300">
        <div className="flex justify-between items-start mb-4">
          <div className="flex-1">
            <div className="flex items-center gap-2 mb-2">
              <h3 className="text-xl font-bold text-white">{product.name}</h3>
              <span className={`px-2 py-1 text-xs rounded-full capitalize ${getTypeColor(product.productType)}`}>
                {product.productType}
              </span>
            </div>
            <div className="flex items-center gap-2 mb-2">
              <span className="px-2 py-1 bg-purple-600/20 text-purple-400 text-xs rounded font-mono">
                {product.keyTag}
              </span>
              <span className="px-2 py-1 bg-gray-600/20 text-gray-400 text-xs rounded">
                {product.minDays}-{product.maxDays} days
              </span>
            </div>
          </div>
        </div>

        <p className="text-gray-400 mb-4 line-clamp-3">{product.description}</p>

        {product.features && product.features.length > 0 && (
          <div className="mb-4">
            <h4 className="text-sm font-semibold text-gray-300 mb-2">Features:</h4>
            <div className="flex flex-wrap gap-1">
              {product.features.slice(0, 4).map((feature, index) => (
                <span key={index} className="px-2 py-1 bg-indigo-600/20 text-indigo-400 text-xs rounded">
                  {feature}
                </span>
              ))}
              {product.features.length > 4 && (
                <span className="px-2 py-1 bg-gray-600/20 text-gray-400 text-xs rounded">
                  +{product.features.length - 4} more
                </span>
              )}
            </div>
          </div>
        )}

        {/* Pricing Model Display */}
        <div className="bg-dark-200 p-4 rounded-lg mb-4">
          <h4 className="text-white font-semibold mb-3">Pricing Model</h4>
          <div className="grid grid-cols-2 gap-4 mb-3">
            <div className="text-center p-2 bg-blue-600/10 rounded">
              <div className="text-blue-400 font-semibold">{product.baseCreditCost}</div>
              <div className="text-xs text-gray-400">Base Cost</div>
            </div>
            <div className="text-center p-2 bg-green-600/10 rounded">
              <div className="text-green-400 font-semibold">{product.creditsPerDay}</div>
              <div className="text-xs text-gray-400">Per Day</div>
            </div>
          </div>
          
          <div className="text-xs text-gray-400 mb-2">Sample costs:</div>
          <div className="space-y-1">
            {sampleCosts.map(({ days, cost }) => (
              <div key={days} className="flex justify-between text-xs">
                <span className="text-gray-400">{days} day{days > 1 ? 's' : ''}:</span>
                <div className="flex items-center gap-2">
                  <span className={`font-semibold ${canAfford(days) ? 'text-white' : 'text-red-400'}`}>
                    {cost} credits
                  </span>
                  {!canAfford(days) && (
                    <span className="text-red-400 text-xs">❌</span>
                  )}
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* User Credits Status */}
        <div className="bg-dark-200 p-3 rounded-lg mb-4">
          <div className="flex justify-between items-center">
            <span className="text-gray-400 text-sm">Your Credits:</span>
            <span className={`font-semibold ${user?.credits >= product.baseCreditCost ? 'text-green-400' : 'text-red-400'}`}>
              {user?.credits || 0}
            </span>
          </div>
          {user?.credits < product.baseCreditCost && (
            <div className="text-red-400 text-xs mt-1">
              ⚠️ Insufficient credits for minimum purchase
            </div>
          )}
        </div>

        <button
          onClick={() => openPurchaseModal(product)}
          disabled={!user || user.credits < product.baseCreditCost || purchasing === product._id}
          className={`w-full py-3 px-4 rounded-lg font-semibold transition-all duration-300 ${
            user && user.credits >= product.baseCreditCost && purchasing !== product._id
              ? 'btn-neon' 
              : 'bg-gray-600 text-gray-400 cursor-not-allowed'
          }`}
        >
          {purchasing === product._id ? (
            <div className="flex items-center justify-center">
              <div className="spinner mr-2"></div>
              Processing...
            </div>
          ) : !user ? (
            'Login to Purchase'
          ) : user.credits < product.baseCreditCost ? (
            'Insufficient Credits'
          ) : (
            `Purchase License`
          )}
        </button>
      </div>
    );
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="spinner"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="card-astral p-6">
        <h1 className="text-3xl font-bold text-white mb-2">Available Products</h1>
        <p className="text-gray-400">
          Browse and purchase license keys for software products. Each product uses a credit-based pricing model.
        </p>
        {user && (
          <div className="mt-4 bg-dark-200 p-4 rounded-lg">
            <div className="flex justify-between items-center mb-3">
              <h3 className="text-white font-semibold">Your Balance</h3>
              <button
                onClick={handleRefreshBalance}
                className="text-blue-400 hover:text-blue-300 text-sm transition-colors"
                title="Refresh balance"
              >
                🔄 Refresh
              </button>
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div className="text-center p-3 bg-blue-600/10 rounded">
                <div className="text-blue-400 font-semibold text-lg">{user?.credits || 0}</div>
                <div className="text-xs text-gray-400">Credits</div>
              </div>
              <div className="text-center p-3 bg-green-600/10 rounded">
                <div className="text-green-400 font-semibold text-lg">${user?.balance?.toFixed(2) || '0.00'}</div>
                <div className="text-xs text-gray-400">USD Balance</div>
              </div>
            </div>
          </div>
        )}
      </div>

      {/* Products Grid */}
      {products.length === 0 ? (
        <div className="card-astral p-8 text-center">
          <h3 className="text-xl font-bold text-gray-400 mb-2">No Products Available</h3>
          <p className="text-gray-500">
            There are currently no active products available for purchase.
          </p>
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {products.map((product) => (
            <ProductCard key={product._id} product={product} />
          ))}
        </div>
      )}

      {/* Purchase Modal */}
      {showPurchaseModal && selectedProduct && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="card-astral p-6 w-full max-w-md mx-4">
            <h2 className="text-xl font-bold text-white mb-4">Purchase License</h2>
            
            <div className="space-y-4">
              <div className="bg-dark-200 p-4 rounded-lg">
                <h3 className="text-white font-semibold mb-2">{selectedProduct.name}</h3>
                <p className="text-gray-400 text-sm">{selectedProduct.description}</p>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-300 mb-2">
                  License Duration (days)
                </label>
                <div className="space-y-2">
                  <input
                    type="range"
                    min={selectedProduct.minDays}
                    max={selectedProduct.maxDays}
                    value={selectedDuration}
                    onChange={(e) => setSelectedDuration(parseInt(e.target.value))}
                    className="w-full"
                  />
                  <div className="flex justify-between text-sm text-gray-400">
                    <span>{selectedProduct.minDays} days</span>
                    <span className="text-white font-semibold">{selectedDuration} days</span>
                    <span>{selectedProduct.maxDays} days</span>
                  </div>
                </div>
              </div>

              <div className="bg-dark-200 p-4 rounded-lg">
                <h4 className="text-white font-semibold mb-2">Cost Breakdown</h4>
                <div className="space-y-2 text-sm">
                  <div className="flex justify-between">
                    <span className="text-gray-400">Base cost:</span>
                    <span className="text-blue-400">{selectedProduct.baseCreditCost} credits</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-gray-400">Duration cost:</span>
                    <span className="text-green-400">{selectedDuration} × {selectedProduct.creditsPerDay} = {selectedDuration * selectedProduct.creditsPerDay} credits</span>
                  </div>
                  <div className="flex justify-between border-t border-gray-600 pt-2 font-semibold">
                    <span className="text-white">Total cost:</span>
                    <span className="text-purple-400">{calculateCost(selectedProduct, selectedDuration)} credits</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-gray-400">Your credits:</span>
                    <span className={user?.credits >= calculateCost(selectedProduct, selectedDuration) ? 'text-green-400' : 'text-red-400'}>
                      {user?.credits || 0} credits
                    </span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-gray-400">After purchase:</span>
                    <span className={user?.credits >= calculateCost(selectedProduct, selectedDuration) ? 'text-white' : 'text-red-400'}>
                      {(user?.credits || 0) - calculateCost(selectedProduct, selectedDuration)} credits
                    </span>
                  </div>
                </div>
              </div>

              <div className="flex gap-3 pt-4">
                <button
                  onClick={handlePurchase}
                  disabled={!user || user.credits < calculateCost(selectedProduct, selectedDuration) || purchasing}
                  className={`flex-1 py-2 px-4 rounded-lg font-semibold transition-all duration-300 ${
                    user && user.credits >= calculateCost(selectedProduct, selectedDuration) && !purchasing
                      ? 'btn-neon' 
                      : 'bg-gray-600 text-gray-400 cursor-not-allowed'
                  }`}
                >
                  {purchasing ? 'Processing...' : `Purchase for ${calculateCost(selectedProduct, selectedDuration)} Credits`}
                </button>
                <button
                  onClick={() => {
                    setShowPurchaseModal(false);
                    setSelectedProduct(null);
                  }}
                  className="btn-neon-blue flex-1"
                  disabled={purchasing}
                >
                  Cancel
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default Products;
